import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import Container from 'react-bootstrap/Container'
import {useIntl} from 'react-intl'
import {
  Button,
  Table,
  TableBody,
  TableForwardRefProps,
  TableWrapper,
  ValidationErrorModal,
} from '../../../../../../_gori/partials/widgets'
import {cloneDeep, get, isEmpty, uniq} from 'lodash'
import {toast} from 'react-toastify'
import {KTSVG} from '../../../../../../_gori/helpers'
import {ConfirmActionModal} from '../../../../../../_gori/components'
import A1_CONSTANTS from '../../../core/_const'
import A1BatchService from '../../../core/_requests'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'
import {AddressView} from '../../../../orders'
import {A1BatchDuplicateModal} from '../../order-filters/A1BatchDuplicateModal'
import {BatchManualInput} from './BatchManualInput'

export type OptionKey = 'new' | 'existing'

const MAX_BATCH_ORDERS = A1_CONSTANTS.UPLOAD_CONFIG.MAX_UPLOAD

type BatchInfo = {
  code: string
  bae_id: string
  customer?: object
  tracking_number?: string
  orderId?: string
  orderNotes?: string
  shipFrom: object
  shipFromString: string
  errorCode?: string
  a1BatchId: number | null
}

const ADDRESS_KEYS = [
  'city',
  'company',
  'country',
  'email',
  'first_name',
  'last_name',
  'phone',
  'state',
  'street1',
  'street2',
  'zip',
]

type BatchScanResultProps = {
  show: boolean
  handleClose: () => void
  setShow: (show: boolean) => void
  type: OptionKey
  handleSuccess: () => void
  batchInfo?: {
    title: string
    batchId?: number
    from?: any
    order_count?: number
  }
}

const BatchScanResult = ({
  show,
  handleClose,
  setShow,
  type,
  handleSuccess,
  batchInfo,
}: BatchScanResultProps) => {
  const intl = useIntl()

  const isExisting = useMemo(() => type === 'existing', [type])

  const [validationErrors, setValidationErrors] = useState<any>()

  const [batches, setBatches] = useState<BatchInfo[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const bufferRef = useRef('')
  const scrollRef = useRef<TableForwardRefProps>(null)
  const [errors, setErrors] = useState<string[]>([])
  const [expandError, setExpandError] = useState<boolean>(false)
  const {newCancelToken, isCancel} = useCancelToken()

  const [modal, setModal] = useState<{
    [key: string]: {
      show: boolean
      data: any
    }
  }>({
    batchesDuplicate: {
      show: false,
      data: [],
    },
    confirmSubmit: {
      show: false,
      data: null,
    },
  })

  const clearModalKey = (key: string) => {
    setModal((prev) => ({
      ...prev,
      [key]: {
        show: false,
        data: null,
      },
    }))
  }

  const addressToString = useCallback(
    (from: Record<string, any>) =>
      Object.keys(from)
        .sort()
        .map((k) => {
          const v = from[k]
          return typeof v === 'string' ? v.trim() : String(v || '')
        })
        .join('|'),
    []
  )

  const shipFromLocationDisplay = (address): string => {
    if (!address || isEmpty(address)) return ''

    const street = [address.street_1, address.street_2].filter(Boolean).join(' ')

    const name = [address.first_name, address.last_name].filter(Boolean).join(' ')

    const parts = [street, address.city, address.state, address.zip]
      .filter(Boolean)
      .map((p) => p?.toString().trim())

    const prefix = [name, address.company].filter(Boolean)

    return [...prefix, ...parts].join(', ')
  }

  const getBatchInfo = useCallback(
    async (code: string) => {
      let newBatch: BatchInfo = {
        code,
        bae_id: '',
        customer: {},
        tracking_number: code,
        orderId: '',
        orderNotes: '',
        shipFrom: {},
        shipFromString: '',
        errorCode: '',
        a1BatchId: null,
      }
      try {
        const rs = await A1BatchService.getA1ScanInformation({
          params: {
            tracking_number: code,
            batch_id: isExisting ? batchInfo?.batchId : undefined,
          },
          cancelToken: newCancelToken(),
        })
        const customer = {}

        const shipFrom = {}
        ADDRESS_KEYS.forEach((key) => {
          customer[key] = rs?.[`to_${key}`]
          shipFrom[key] = rs?.[`from_${key}`]
        })

        newBatch = {
          code,
          bae_id: rs?.code,
          customer,
          tracking_number: code,
          orderId: rs?.id ?? '',
          orderNotes: rs?.note ?? '',
          shipFrom,
          shipFromString: addressToString(shipFrom),
          errorCode: '',
          a1BatchId: rs?.a1_batch_id ?? null,
        }
      } catch (error: any) {
        newBatch = {
          ...newBatch,
          errorCode: get(error, 'response.data.error.message', 'SCAN_NOT_FOUND'),
        }
        if (isCancel(error)) return
      } finally {
        setBatches((prev) => [...prev, newBatch])
        setTimeout(() => scrollRef.current?.scrollToBottom(), 200)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [batchInfo?.batchId, isExisting]
  )

  const handleScan = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (!bufferRef.current) return

        if (batches.length === MAX_BATCH_ORDERS) {
          toast.error(intl.formatMessage({id: 'MAXIMUM_ORDERS_SCAN'}, {max: MAX_BATCH_ORDERS}))
        } else {
          getBatchInfo(bufferRef.current)
        }

        bufferRef.current = ''
      } else if (!!e.key.length && !e.ctrlKey && !e.metaKey && !e.altKey) {
        bufferRef.current += e.key
      }
    },
    [batches.length, getBatchInfo, intl]
  )

  const handleRemoveBatch = useCallback(
    (code: string) => {
      const _batches = cloneDeep(batches)
      const index = batches.findIndex((batch) => batch.code === code)
      if (index === -1) return

      _batches.splice(index, 1)
      setBatches(_batches)
    },
    [batches]
  )

  const duplicates = useMemo(() => {
    const idCount: Record<string, number> = {}
    if (batches.length < 2) return Array(batches.length).fill(false)
    return batches.map((item, idx) => {
      if (!item.code) return false
      idCount[item.code] = (idCount[item.code] || 0) + 1
      const previousSameIdIndex = batches.findIndex((i, iIdx) => i.code === item.code && iIdx < idx)
      return previousSameIdIndex !== -1
    })
  }, [batches])

  const diffShipFrom: boolean[] = useMemo(() => {
    if (isExisting && batchInfo?.order_count) {
      const existingShipFrom = addressToString(batchInfo.from)

      return batches.map((item) => {
        return item.shipFromString !== existingShipFrom && !item.errorCode
      })
    }

    if (batches.length < 2) {
      return Array(batches.length).fill(false)
    }

    return batches.map((item, idx) => {
      if (item.errorCode) return false
      if (!item.shipFromString) return false

      const hasDifferent = batches.some(
        (i, iIdx) => iIdx < idx && i.shipFromString && i.shipFromString !== item.shipFromString
      )

      return hasDifferent
    })
  }, [batchInfo?.from, batchInfo?.order_count, batches, isExisting, addressToString])
  const errorStatusCellString = useCallback(
    (index: number, errorCode?: string) => {
      if (duplicates[index]) return 'DUPLICATE'
      if (errorCode === 'SCAN_NOT_FOUND') return 'DOES_NOT_EXIST'
      if (diffShipFrom[index]) return 'MISMATCH_ADDRESS'
      if (
        errorCode === 'SCAN_ORDER_ALREADY_IN_BATCH' ||
        errorCode === 'SCAN_ORDER_ALREADY_IN_A1_BATCH'
      )
        return 'BATCH_CONFLICT'

      if (errorCode === 'SCAN_ORDER_ALREADY_IN_THIS_A1_BATCH') return 'DUPLICATE'

      return errorCode || ''
    },
    [diffShipFrom, duplicates]
  )

  const validBatches = useMemo(() => {
    return batches.filter((item, idx) => {
      const isDuplicate = duplicates[idx]
      const isDiffShipFrom = diffShipFrom[idx]
      const hasError = !!item.errorCode

      return !isDuplicate && !isDiffShipFrom && !hasError
    })
  }, [batches, duplicates, diffShipFrom])

  const batchShipFrom = useMemo(() => {
    if (isExisting) {
      return batchInfo?.from || {}
    }

    const firstValid = batches.find((item, idx) => {
      const isDuplicate = duplicates[idx]
      const isDiffShipFrom = diffShipFrom[idx]
      const hasError = !!item.errorCode

      return !isDuplicate && !isDiffShipFrom && !hasError && item.shipFrom
    })

    return firstValid?.shipFrom
  }, [isExisting, batches, batchInfo?.from, duplicates, diffShipFrom])

  const columns = useMemo(() => {
    return [
      {
        id: 'bae_id',
        Header: intl.formatMessage({id: 'BAE_ID'}),
        Cell: ({row}: {row: any}) => row.original?.bae_id,
      },
      {
        id: 'customer',
        Header: intl.formatMessage({id: 'CUSTOMER'}),
        Cell: ({row}: {row: any}) => <AddressView data={row.original?.customer || {}} />,
      },
      {
        id: 'tracking_number',
        Header: intl.formatMessage({id: 'TRACKING_NUMBER'}),
        Cell: ({row}: {row: any}) => (
          <span className='text-primary'>{row.original?.tracking_number}</span>
        ),
      },
      {
        id: 'orderId',
        Header: intl.formatMessage({id: 'ORDER_REF'}),
        Cell: ({row}: {row: any}) => row.original?.orderId,
      },
      {
        id: 'status',
        Header: intl.formatMessage({id: 'SCAN_STATUS'}),
        Cell: ({row}: {row: any}) => {
          const errorMessage = errorStatusCellString(row.index, row.original?.errorCode)
          return (
            <div className='min-w-180px'>
              <div className='d-flex align-items-center gap-2'>
                {errorMessage ? (
                  <>
                    <KTSVG path='/media/gori/invalid.svg' className='svg-icon-1' />
                    <span className='text-danger fw-normal'>
                      {intl.formatMessage({id: errorMessage})}
                    </span>
                  </>
                ) : (
                  <>
                    <KTSVG path='/media/gori/valid.svg' className='svg-icon-2' />
                    <span className='text-primary fw-normal'>
                      {intl.formatMessage({id: 'SUCCESSFUL'})}
                    </span>
                  </>
                )}
              </div>
            </div>
          )
        },
      },
      {
        id: 'action',
        Cell: ({row}) => {
          return (
            <div className='cursor-pointer' onClick={() => handleRemoveBatch(row.original.code)}>
              <KTSVG
                path='/media/gori/orders/empty.svg'
                className='svg-icon-3 text-gray-700 text-hover-danger'
                small={false}
              />
            </div>
          )
        },
      },
    ]
  }, [intl, errorStatusCellString, handleRemoveBatch])

  const handleCloseConfirmSubmit = useCallback(() => {
    clearModalKey('confirmSubmit')
    setShow(true)
  }, [setShow])

  const handleSubmitBatches = useCallback(async () => {
    setIsLoading(true)
    try {
      await A1BatchService.store(modal.confirmSubmit.data)
      toast.success(intl.formatMessage({id: 'ADDED_TO_A1_BATCH_SUCCESSFULLY'}))
      setBatches([])
      handleClose()
      handleSuccess()
    } catch (error: any) {
      if (isCancel(error)) return
      setValidationErrors(error?.response)
    } finally {
      clearModalKey('confirmSubmit')
      setIsLoading(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validBatches, type, batchInfo?.title, batchInfo?.batchId, intl, handleClose])

  const renderError = useMemo(() => {
    if (!errors.length) return null

    if (errors.length === 1) {
      return (
        <div className='d-flex align-items-center gap-1 px-3 py-2 bg-light-danger mb-6 gap-1'>
          <KTSVG path={'/media/gori/invalid.svg'} className='svg-icon-3' />

          <span className='fs-6 text-danger'>{intl.formatMessage({id: errors[0]})}</span>
        </div>
      )
    }
    return (
      <div className='gap-1 px-3 py-2 bg-light-danger mb-6'>
        <div className='d-flex align-items-center justify-content-between'>
          <div className='d-flex align-items-center gap-1'>
            <KTSVG path={'/media/gori/invalid.svg'} className='svg-icon-3' />

            <span className='fs-6 text-danger'>
              {intl.formatMessage({id: 'SCAN_ERRORS'}, {count: errors.length})}
            </span>
          </div>
          <div onClick={() => setExpandError((prev) => !prev)} className='w-10 h-10 cursor-pointer'>
            <KTSVG
              path={`/media/gori/arrows/${expandError ? 'arrow_up' : 'arrow_down'}.svg`}
              className='svg-icon-1'
            />
          </div>
        </div>

        {expandError && (
          <div>
            <ul className='mt-1'>
              {errors.map((error) => (
                <li key={error} className='text-danger fw-light'>
                  {intl.formatMessage({id: error})}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  }, [errors, expandError, intl])

  const handleCloseAndReset = useCallback(() => {
    setBatches([])
    handleClose()
  }, [handleClose])

  const totalFailed = useMemo(() => {
    return batches.reduce((count, item, idx) => {
      const isDuplicate = duplicates[idx]
      const isDiffShipFrom = diffShipFrom[idx]
      const hasError = !!item.errorCode
      return count + (isDuplicate || isDiffShipFrom || hasError ? 1 : 0)
    }, 0)
  }, [batches, duplicates, diffShipFrom])

  useEffect(() => {
    if (!batches.length) {
      setErrors([])
      setExpandError(false)
      return
    }

    const newErrors: string[] = []

    const isDuplicated = duplicates.some((item) => item)
    if (isDuplicated) newErrors.push('SCAN_DUPLICATED')

    const isDiffShipFrom = diffShipFrom.some((item) => item)
    if (isDiffShipFrom) newErrors.push('SCAN_DIFF_SHIP_FROM')

    const errorCodes = batches.map((item) => item.errorCode).filter((err): err is string => !!err)
    newErrors.push(...uniq(errorCodes))

    setErrors(uniq(newErrors))
  }, [batches, duplicates, diffShipFrom])

  const handleDuplicateClose = () => {
    clearModalKey('batchesDuplicate')
    setBatches([])
    setValidationErrors(undefined)
    handleClose()
  }

  const handleDuplicateSuccess = () => {
    handleSuccess()
    clearModalKey('batchesDuplicate')
    setBatches([])
    setValidationErrors(undefined)
    handleClose()
  }

  const onClickFinalize = () => {
    const {hasBatch, noBatch} = validBatches.reduce(
      (acc: {hasBatch: string[]; noBatch: string[]}, batch: BatchInfo) => {
        const key = batch.a1BatchId ? 'hasBatch' : 'noBatch'
        if (batch.orderId) acc[key].push(batch.orderId)

        return acc
      },
      {hasBatch: [], noBatch: []}
    )

    const payload = {
      name: isExisting ? null : batchInfo?.title,
      existing_batch: isExisting ? batchInfo?.batchId : null,
      no_batch: noBatch.filter((item) => item !== null),
      has_batch: hasBatch.filter((item) => item !== null),
      type: isExisting ? 'move' : 'create',
      is_scan: true,
    }

    if (hasBatch.length) {
      setModal((prev) => ({
        ...prev,
        batchesDuplicate: {
          show: true,
          data: {
            payload,
            name_position: batchInfo?.title || '',
          },
        },
      }))
      return
    }

    setModal((prev) => ({
      ...prev,
      confirmSubmit: {
        show: true,
        data: payload,
      },
    }))
  }

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      {modal.batchesDuplicate.show && (
        <A1BatchDuplicateModal
          show={modal.batchesDuplicate.show}
          data={modal.batchesDuplicate.data}
          handleClose={handleDuplicateClose}
          handleSuccess={handleDuplicateSuccess}
        />
      )}
      <ConfirmActionModal
        show={modal.confirmSubmit.show}
        classNameDialog='modal-size-lg'
        title={intl.formatMessage({id: 'FINALIZE_BATCH_SCAN'})}
        message={
          <span className='fw-bold text-center'>
            <p>{intl.formatMessage({id: 'ARE_YOU_SURE_YOU_WANT_TO_PROCEED'})}</p>
            <p>
              {intl.formatMessage({
                id: 'FINALIZE_BATCH_SCAN_CONFIRM',
              })}
            </p>
          </span>
        }
        messagePrimary={intl.formatMessage({id: 'SUBMIT'})}
        messageSecondary={intl.formatMessage({id: 'CANCEL'})}
        handlePrimaryAction={handleSubmitBatches}
        handleSecondaryAction={handleCloseConfirmSubmit}
        handleClose={handleCloseConfirmSubmit}
        loadingPrimary={isLoading}
      />
      <Modal
        id='gori_modal_a1_batch_scan_result'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='modal-size-xl h-auto'
        contentClassName='modal-padding'
        show={show}
        backdrop='static'
        onHide={handleCloseAndReset}
      >
        <Modal.Header closeButton>
          <Modal.Title bsPrefix={'fw-bolder fs-1'}>
            {intl.formatMessage({
              id: type === 'new' ? 'SCAN_NEW_A1_BATCH' : 'SCAN_TO_EXISTING_A1_BATCH',
            })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <div className='border rounded-3 p-6 border-primary'>
              <h3>{batchInfo?.title}</h3>
              <h5 className='text-gray-600 mt-1 fw-normal'>
                {intl.formatMessage({id: 'SHIP_FROM_LOCATION'})}:{' '}
                {batchShipFrom ? shipFromLocationDisplay(batchShipFrom) : 'N/A'}
              </h5>
              <div className='d-flex align-items-center justify-content-between mt-6 mb-6'>
                <h5 className='fw-bold mb-0 text-dark'>
                  {intl.formatMessage({id: 'PREVIEW_ADDED_SCAN_ORDER'})}
                </h5>
                {!!batches.length && (
                  <div className='d-flex align-items-center gap-3 fw-bold'>
                    <div className='text-primary'>
                      {intl.formatMessage({id: 'TOTAL_SCANNED'})}: {batches.length}
                    </div>
                    <div className='text-danger'>
                      {intl.formatMessage({id: 'TOTAL_FAILED'})}: {totalFailed}
                    </div>
                  </div>
                )}
              </div>
              {renderError}
              {batches.length ? (
                <TableWrapper fullWidth={false}>
                  <TableBody height={'42vh'} minHeight={'42vh'} className='p-0 fw-bold'>
                    <Table ref={scrollRef} columns={columns} data={batches} />
                  </TableBody>
                </TableWrapper>
              ) : (
                <div className='dropzone border-primary border-2 mt-6 h-150px d-flex justify-content-center align-items-center bg-gray-450'>
                  <h3 className='fw-bold text-gray-700'>
                    {intl.formatMessage({id: 'START_SCANNING_TO_PREVIEW_ORDERS'})}
                  </h3>
                </div>
              )}
            </div>
            <BatchManualInput getBatchInfo={getBatchInfo} handleScan={handleScan} />
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <div className='d-flex justify-content-end'>
            <Button
              className='btn btn-advanced-secondary me-3'
              label={intl.formatMessage({id: 'CANCEL'})}
              event={handleCloseAndReset}
            />
            <Button
              className='btn btn-advanced-primary'
              label={intl.formatMessage({id: 'FINALIZE_BATCH_SCAN'})}
              loadingText={intl.formatMessage({id: 'FINALIZE_BATCH_SCAN'})}
              disabled={!validBatches.length}
              event={() => {
                onClickFinalize()
                setShow(false)
              }}
            />
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export {BatchScanResult}
